import { captureException } from "@sentry/react";
import { useAtomValue } from "jotai";
import { omit } from "lodash-es";
import { toast } from "react-hot-toast";
import { UnsplashService } from "../../../../../server/api/unsplash";
import { useToken } from "../../../../../server/auth/use-token";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { useEmailMomentService } from "../../../../email-moment/hooks/use-email-moment-service";
import { SupabaseFollowUpService } from "../../../../follow-up/server/follow-up-service";
import { useMessageBodyCreator } from "../../../../message/editor/utils/create-message-body/create-message-body";
import { editorIsEmpty } from "../../../../message/editor/utils/editor-is-empty";
import { SMS_MAX_CHARACTERS } from "../../../../message/editor/utils/sms-character-count";
import { SupabasePollService } from "../../../../poll/server/supabase-poll-service";
import { useScheduleService } from "../../../../schedule/hooks/use-schedule-service";
import { useSegmentSaveAllowed } from "../../../../segment/hooks/use-segment-save-allowed";
import { useSegmentService } from "../../../../segment/hooks/use-segment-service";
import { persistUnlayerPolls } from "../../../../unlayer-email-content/server/persist-unlayer-polls";
import {
  SupabaseWhatsAppMomentService,
  normaliseWhatsAppMomentForSave,
} from "../../../../whatsapp-moment/server/whatsapp-moment-service";
import {
  SupabaseHyperlinkService,
  upsertHyperlinks,
} from "../../../server/supabase-hyperlink-service";
import { SupabaseMomentService } from "../../../server/supabase-moment-service";
import { unsplashDownloadUrlAtom } from "../../../types/store";
import { useRoundaboutContext } from "../../roundabout/roundabout-context";

import type { SaveButtonResponse } from "../types";
import type { HandleSaveMoment } from "./types";

interface UseHandleSaveConfig {
  reset: (values: HandleSaveMoment) => void;
  validate: () => Promise<boolean>;
  errorHandlers?: {
    segment?: (err: Error) => void;
    design?: (err: Error) => void;
  };
  refreshCoverImage?: (url?: string | null) => void;
}

export function didPersistMoment(result: SaveButtonResponse): boolean {
  return result?.status === "valid" || result?.status === "invalid";
}

export function useHandleSave({
  reset,
  validate,
  errorHandlers,
  refreshCoverImage,
}: UseHandleSaveConfig): {
  loading: boolean;
  callback: (vals: HandleSaveMoment) => Promise<SaveButtonResponse>;
} {
  const getToken = useToken();

  const { data: segment_save_allowed } = useSegmentSaveAllowed();

  const saveSegment = useSegmentService();

  const saveEmail = useEmailMomentService();
  const saveSchedule = useScheduleService();
  const saveFollowUp = useSupabaseCallback(
    async ({ supabase }, follow_up: HandleSaveMoment["follow_up"]) => {
      if (!follow_up) return;
      const { error } = await new SupabaseFollowUpService(supabase).upsert(follow_up);
      if (error) throw error;
    },
    [],
  );

  const { data: createMessageBody, loading } = useMessageBodyCreator();
  const unplashDownloadUrl = useAtomValue(unsplashDownloadUrlAtom);
  const { captureMomentFormSave } = useRoundaboutContext();

  return {
    loading,
    callback: useSupabaseCallback(
      async ({ supabase, account_id }, vals: HandleSaveMoment): Promise<SaveButtonResponse> => {
        if (loading) return { status: "error" };
        if (!account_id) {
          toast.error("Account not found");
          return { status: "error" };
        }

        if (!createMessageBody) {
          toast.error("No message body creator");
          return { status: "error" };
        }

        try {
          if (segment_save_allowed) await saveSegment(vals.segment ?? null);
        } catch (error) {
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          errorHandlers?.segment?.(error as Error);
          return { status: "error" };
        }

        let valuesToSave = vals;

        try {
          await saveSchedule(vals.schedule);
          if (!vals.use_lexical_editor) {
            if (vals.email_moment) {
              const persistedPolls = await persistUnlayerPolls({
                accountId: account_id,
                emailMoment: vals.email_moment,
                existingPollIds: vals.poll_ids ?? [],
                pollService: new SupabasePollService(supabase),
              });

              if (persistedPolls.pollIds.length > 0 || vals.poll_ids?.length) {
                valuesToSave = {
                  ...vals,
                  email_moment: persistedPolls.emailMoment,
                  poll_ids: persistedPolls.pollIds,
                };
              }
            }

            await saveEmail(valuesToSave.email_moment ?? null);
          }
          await saveFollowUp(valuesToSave.follow_up ?? null);
        } catch (error) {
          captureException(error);
          return { status: "error" };
        }
        if (valuesToSave.program_id === undefined) return { status: "error" };

        let whatsappMomentId: HandleSaveMoment["whatsapp_moment_id"] = null;
        if (valuesToSave.channel === "whatsapp" && valuesToSave.whatsapp_moment) {
          const { error: whatsappError, data: savedWhatsAppMoment } =
            await new SupabaseWhatsAppMomentService(supabase).upsert(
              normaliseWhatsAppMomentForSave(valuesToSave.whatsapp_moment),
            );
          if (whatsappError || !savedWhatsAppMoment) {
            toast.error("Could not save the WhatsApp destination");
            console.error({ whatsappError });
            return { status: "error" };
          }
          whatsappMomentId = savedWhatsAppMoment.id;
        }
        const { data: messageBodyData, errorMessage } = await createMessageBody({
          editorState: editorIsEmpty(valuesToSave.design) ? null : valuesToSave.design,
          channel: valuesToSave.channel,
          account_id,
          email_design_id: valuesToSave.email_design_id ?? undefined,
          poll_ids: valuesToSave.poll_ids ?? undefined,
        });
        if (errorMessage) {
          toast.error("Something went wrong. Please try again.");
          return { status: "error" };
        }

        const valid_on_save = await validate();
        const { error: saveError, data } = await new SupabaseMomentService(supabase).upsert({
          ...omit(
            valuesToSave,
            "sent",
            "running",
            "composeMethods",
            "status",
            "review_status",
            "reviewer_person_id",
            "schedule",
            "segment",
            "email_moment",
            "chat_moment",
            "text_moment",
            "used_merge_fields",
            "unrecoverable_error",
            "static_cc",
            "static_bcc",
            "dynamic_cc",
            "dynamic_bcc",
            "extracted_survey_ids",
            "follow_up",
            "whatsapp_moment",
          ),
          program_id: valuesToSave.program_id,
          schedule_id: valuesToSave.schedule?.id ?? null,
          segment_id: valuesToSave.segment?.id ?? null,
          email_moment_id: valuesToSave.email_moment?.id ?? null,
          chat_moment_id: null, // deprecated: old-style chat moments have been archived
          follow_up_id: valuesToSave.follow_up?.id ?? null,
          whatsapp_moment_id: whatsappMomentId,
          body: messageBodyData?.body ?? null,
          body_text: messageBodyData?.text ?? null,
          valid_on_save,
        });

        const valid = !(saveError || !data);

        if (!valid) toast.error("Something went wrong. Please try again.");

        if (
          valid &&
          messageBodyData?.body &&
          valuesToSave.id &&
          valuesToSave.channel &&
          valuesToSave.channel !== "whatsapp"
        ) {
          try {
            await upsertHyperlinks(
              messageBodyData.body,
              valuesToSave.channel,
              valuesToSave.id,
              new SupabaseHyperlinkService(supabase),
            );
          } catch (error) {
            captureException(error);
          }
        }

        if (
          valid &&
          !valuesToSave.use_lexical_editor &&
          valuesToSave.email_moment?.body &&
          valuesToSave.id &&
          valuesToSave.channel &&
          valuesToSave.channel !== "whatsapp"
        ) {
          try {
            await upsertHyperlinks(
              valuesToSave.email_moment.body,
              valuesToSave.channel,
              valuesToSave.id,
              new SupabaseHyperlinkService(supabase),
            );
          } catch (error) {
            captureException(error);
          }
        }

        if (refreshCoverImage?.(valuesToSave.cover_image_path) && unplashDownloadUrl) {
          void new UnsplashService(await getToken())
            .ackDownload(unplashDownloadUrl)
            .catch(console.error);
        }

        const resetValues = {
          ...valuesToSave,
          whatsapp_moment_id: whatsappMomentId,
        };
        reset(resetValues);

        const validationError = validateMomentContent(
          messageBodyData?.body ?? undefined,
          valuesToSave.channel ?? undefined,
          messageBodyData?.characterCount,
        );
        if (validationError) {
          errorHandlers?.design?.(new Error(validationError.error));
          return { status: "invalid", message: validationError.error };
        }
        if (valid) {
          captureMomentFormSave();
          if (valuesToSave.channel === "whatsapp" && !valuesToSave.whatsapp_moment_id) {
            // oxlint-disable-next-line unicorn/prefer-global-this
            window.pendo?.track("whatsapp-communication-created", { momentId: valuesToSave.id });
          }
        }
        return { status: valid ? "valid" : "error" };
      },
      [
        loading,
        createMessageBody,
        validate,
        refreshCoverImage,
        unplashDownloadUrl,
        reset,
        segment_save_allowed,
        saveSegment,
        saveSchedule,
        saveEmail,
        getToken,
        captureMomentFormSave,
        errorHandlers,
        saveFollowUp,
      ],
    ),
  };
}

export const validateMomentContent = (
  body?: string,
  channel?: string,
  smsCharacterCount?: number,
): { error: string } | undefined => {
  // Validate the estimated SMS length (survey links / links / merge tags counted as their
  // shortened / estimated length, matching the editor counter) rather than the raw outgoing
  // body, which contains full unshortened survey URLs. Fall back to body length if absent.
  const smsLength = smsCharacterCount ?? body?.length;
  if (channel === "twilio" && smsLength !== undefined && smsLength > SMS_MAX_CHARACTERS) {
    return {
      error: `Message is too long: message limit (${smsLength}/${SMS_MAX_CHARACTERS})`,
    };
  }

  if (!body || channel !== "slack") return undefined;
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const { blocks } = JSON.parse(body) as {
    blocks: { type: string; text?: string }[];
  };

  const slackMaxBlocks = 50;
  if (blocks.length > slackMaxBlocks) {
    return {
      error: `Message is too long: over ${slackMaxBlocks} paragraph limit (${blocks.length}/${slackMaxBlocks})`,
    };
  }
  const slackMaxBlockLength = 2500;
  if (blocks.some(({ text }) => text && text.length > slackMaxBlockLength)) {
    return { error: `Paragraph is too long; paragraph limit (${slackMaxBlockLength})` };
  }
  return undefined;
};
